import React, { useState, useEffect, useCallback } from 'react';
import { toUrl } from '../lib/config';

interface GorusOneriRow {
  id: number;
  calisaninAdi?: string;
  calistigiBirim: string;
  oneriGorusTarihi: string;
  gorusVeOnerileriniz: string;
  createdAt: string;
}

const HastaneCalisanlariGorusOneriYonetim: React.FC = () => {
  const [gorusler, setGorusler] = useState<GorusOneriRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGorus, setSelectedGorus] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchGorusler = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const url = toUrl('/api/hastane-calisanlari-gorus-oneri');
      const res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Error response:', errorText);
        throw new Error(`Görüş ve öneriler yüklenemedi: ${res.status}`);
      }
      const data = await res.json();
      setGorusler(data.data || []);
    } catch (e: any) {
      console.error('Fetch error:', e);
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGorusler();
  }, [fetchGorusler]);

  const openDeleteModal = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/hastane-calisanlari-gorus-oneri/${deleteId}`), {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Silme işlemi başarısız');
      closeDeleteModal();
      await fetchGorusler();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetail = async (id: number) => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/hastane-calisanlari-gorus-oneri/${id}`));
      if (!res.ok) throw new Error('Görüş/öneri detayı yüklenemedi');
      const data = await res.json();
      setSelectedGorus(data.data);
      setShowDetailModal(true);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredGorusler = gorusler.filter(gorus =>
    gorus.calisaninAdi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gorus.calistigiBirim?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gorus.gorusVeOnerileriniz?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-page" style={{ padding: 20 }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Hastane Çalışanları Görüş ve Öneri Yönetimi</h2>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          Toplam: {gorusler.length} görüş/öneri
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 6 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Çalışan adı, birim veya görüş içeriğine göre ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 15px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14
          }}
        />
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 20 }}>Yükleniyor...</div>}

      {!loading && filteredGorusler.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          Henüz görüş/öneri bulunmuyor.
        </div>
      )}

      {!loading && filteredGorusler.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>ID</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Çalışan Adı</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Birim</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Tarih</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Görüş/Öneri</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, fontSize: 13 }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredGorusler.map((gorus, idx) => (
                <tr key={gorus.id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={{ padding: 12, fontSize: 13 }}>{gorus.id}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>
                    {gorus.calisaninAdi || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Anonim</span>}
                  </td>
                  <td style={{ padding: 12, fontSize: 13 }}>{gorus.calistigiBirim || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{formatDate(gorus.oneriGorusTarihi)}</td>
                  <td style={{ padding: 12, fontSize: 13, maxWidth: 300 }}>
                    <div style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}>
                      {gorus.gorusVeOnerileriniz}
                    </div>
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <button
                      onClick={() => handleShowDetail(gorus.id)}
                      style={{
                        padding: '6px 12px',
                        marginRight: 8,
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Detay
                    </button>
                    <button
                      onClick={() => openDeleteModal(gorus.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Silme Onay Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeDeleteModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: 24,
              maxWidth: 400,
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', color: '#991b1b' }}>Görüş/Öneriyi Sil</h3>
            <p style={{ margin: '0 0 24px 0', color: '#374151' }}>
              Bu görüş/öneriyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={closeDeleteModal}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detay Modal */}
      {showDetailModal && selectedGorus && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              maxWidth: 700,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 24
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Görüş/Öneri Detayı - ID: {selectedGorus.id}</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                Kapat
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <DetailItem 
                label="Çalışan Adı" 
                value={selectedGorus.calisaninAdi || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Anonim</span>} 
              />
              <DetailItem label="Çalıştığı Birim" value={selectedGorus.calistigiBirim} />
              <DetailItem label="Öneri/Görüş Tarihi" value={formatDate(selectedGorus.oneriGorusTarihi)} />
              <DetailItem label="Kayıt Tarihi" value={formatDate(selectedGorus.createdAt)} />
            </div>

            <div style={{ paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ marginBottom: 12 }}>Görüş ve Öneriler</h4>
              <div style={{ 
                padding: 16, 
                backgroundColor: '#f9fafb', 
                borderRadius: 6, 
                fontSize: 14,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}>
                {selectedGorus.gorusVeOnerileriniz}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{value || '-'}</div>
  </div>
);

export default HastaneCalisanlariGorusOneriYonetim;
